import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { usePermission } from "../../hooks/usePermission";
import api from "../../utils/api";
import Card from "../../components/Common/Card";
import Button from "../../components/Common/Button";
import Select from "../../components/Common/Select";
import { showSuccess, showError } from "../../utils/swal";
import { Shield, Lock, Unlock, Save, CheckCircle2, XCircle } from "lucide-react";

const DepartmentPermissions = () => {
    const [departments, setDepartments] = useState([]);
    const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
    const [modules, setModules] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { user } = useAuth();
    const { canView } = usePermission();

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (selectedDepartmentId) {
            fetchPermissions(selectedDepartmentId);
        } else {
            setPermissions([]);
        }
    }, [selectedDepartmentId]);

    const fetchInitialData = async () => {
        try {
            const [deptRes, modRes] = await Promise.all([
                api.get("/departments"),
                api.get("/department-permissions/modules")
            ]);
            setDepartments(deptRes.data.data || deptRes.data);
            setModules(modRes.data.data);
            setLoading(false);
        } catch (error) {
            console.error("Başlangıç verileri yüklenemedi:", error);
            showError("Veriler yüklenirken bir hata oluştu.");
            setLoading(false);
        }
    };

    const fetchPermissions = async (id) => {
        try {
            const response = await api.get(`/departments/${id}/permissions`);
            setPermissions(response.data.data);
        } catch (error) {
            console.error("Yetkiler yüklenemedi:", error);
            showError("Departman yetkileri yüklenemedi.");
        }
    };

    const handleTogglePermission = (moduleKey, action) => {
        const existingPerm = permissions.find(p => p.module === moduleKey);
        
        let newPermissions;
        if (existingPerm) {
            newPermissions = permissions.map(p => 
                p.module === moduleKey ? { ...p, [action]: !p[action] } : p
            );
        } else {
            // Yeni bir kayıt objesi oluştur
            const newPerm = {
                module: moduleKey,
                can_view: false,
                can_create: false,
                can_edit: false,
                can_delete: false,
                can_change_status: false,
                can_share: false,
                can_export: false,
                can_view_all: false,
                can_assign: false,
                [action]: true
            };
            newPermissions = [...permissions, newPerm];
        }
        setPermissions(newPermissions);
    };

    const handleSave = async () => {
        if (!selectedDepartmentId) return;
        
        setSaving(true);
        try {
            await api.put(`/departments/${selectedDepartmentId}/permissions`, {
                permissions: permissions
            });
            showSuccess("Yetkiler başarıyla kaydedildi.");
        } catch (error) {
            console.error("Yetkiler kaydedilemedi:", error);
            showError("Yetkiler kaydedilirken bir hata oluştu.");
        } finally {
            setSaving(false);
        }
    };

    const getPermissionValue = (moduleKey, action) => {
        const perm = permissions.find(p => p.module === moduleKey);
        return perm ? perm[action] : false;
    };

    if (loading) return <div className="text-center py-8">Yükleniyor...</div>;

    if (!canView('departments')) {
        return <div className="p-8 text-center text-red-600">Bu sayfaya erişim yetkiniz bulunmamaktadır.</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <Shield className="text-primary-600" />
                    Departman Yetki Yönetimi
                </h1>
                <Button 
                    onClick={handleSave} 
                    disabled={!selectedDepartmentId || saving}
                    className="flex items-center gap-2"
                >
                    <Save size={18} />
                    {saving ? "Kaydediliyor..." : "Tümünü Kaydet"}
                </Button>
            </div>

            <Card>
                <div className="max-w-md">
                    <Select
                        label="Departman Seçiniz"
                        value={selectedDepartmentId}
                        onChange={(e) => setSelectedDepartmentId(e.target.value)}
                        options={departments.map(d => ({ value: d.id, label: d.name }))}
                    />
                </div>
            </Card>

            {selectedDepartmentId ? (
                <Card noPadding>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="p-4 font-semibold text-gray-700">Modül</th>
                                    <th className="p-4 font-semibold text-gray-700 text-center">Görüntüleme</th>
                                    <th className="p-4 font-semibold text-gray-700 text-center">Ekleme</th>
                                    <th className="p-4 font-semibold text-gray-700 text-center">Düzenleme</th>
                                    <th className="p-4 font-semibold text-gray-700 text-center">Silme</th>
                                    <th className="p-4 font-semibold text-gray-700 text-center">Durum Değ.</th>
                                    <th className="p-4 font-semibold text-gray-700 text-center">Paylaşım</th>
                                    <th className="p-4 font-semibold text-gray-700 text-center">Dışa Aktar</th>
                                    <th className="p-4 font-semibold text-gray-700 text-center text-primary-600">Tümü</th>
                                    <th className="p-4 font-semibold text-gray-700 text-center text-primary-600">Atama</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {modules.map((mod, index) => (
                                    <tr key={mod.key} className={index % 2 === 0 ? "bg-white" : "bg-gray-50/30"}>
                                        <td className="p-4 font-medium text-gray-800">{mod.label}</td>
                                        {[
                                            'can_view', 'can_create', 'can_edit', 'can_delete', 
                                            'can_change_status', 'can_share', 'can_export', 
                                            'can_view_all', 'can_assign'
                                        ].map(action => {
                                            const isAvailable = mod.actions.includes(action);
                                            const isActive = getPermissionValue(mod.key, action);
                                            
                                            return (
                                                <td key={action} className="p-4 text-center">
                                                    {isAvailable ? (
                                                        <button
                                                            onClick={() => handleTogglePermission(mod.key, action)}
                                                            className={`p-1.5 rounded-lg transition-colors ${
                                                                isActive 
                                                                ? "text-green-600 bg-green-50 hover:bg-green-100" 
                                                                : "text-gray-400 bg-gray-100 hover:bg-gray-200"
                                                            }`}
                                                        >
                                                            {isActive ? <Unlock size={20} /> : <Lock size={20} />}
                                                        </button>
                                                    ) : (
                                                        <span className="text-gray-200">—</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 text-gray-500">
                    Lütfen yetkilerini düzenlemek istediğiniz bir departman seçiniz.
                </div>
            )}
        </div>
    );
};

export default DepartmentPermissions;
